import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Sun, BatteryCharging, Building2, Home, Stethoscope, UtensilsCrossed, GraduationCap, Truck, CheckCircle2 } from 'lucide-react';
import { trackEvent, EVENTS } from '@/lib/analytics';
import { SEO } from '@/components/SEO';

const SOLUTIONS = [
  {
    icon: Sun,
    title: 'Solar + Storage',
    desc: 'Enhance commercial or residential solar systems with BESS for peak-shaving, essential backup, and truly zero-emission power.',
    features: ['Peak demand shaving', 'Solar energy time-shifting', 'Grid independence', 'Zero-emission backup'],
    image: '/images/voltide-solar-ev.png',
    color: 'amber',
  },
  {
    icon: BatteryCharging,
    title: 'DG Replacement',
    desc: 'Replace noisy, polluting diesel generators with clean, silent battery energy storage. Lower costs, zero emissions.',
    features: ['Silent operation', 'Zero emissions', 'Lower fuel costs', 'No maintenance hassle'],
    image: '/images/voltide-battery-ev-charger.png',
    color: 'blue',
  },
];

const SECTORS = [
  { icon: UtensilsCrossed, title: 'Restaurants', desc: 'Silent, zero-emission power for dense urban areas. Compact footprint.', image: '/images/voltide-restaurant.png' },
  { icon: Stethoscope, title: 'Hospitals', desc: 'Critical, uninterrupted backup. Silent, pollution-free environment.', image: '/images/voltide-hospital.png' },
  { icon: GraduationCap, title: 'Universities', desc: 'Campus-wide reliable backup with smart energy management.', image: '/images/voltide-university.png' },
  { icon: Home, title: 'Residential', desc: 'Replace noisy diesel gensets. Solar + Storage for communities.', image: '/images/voltide-apartment.png' },
  { icon: Building2, title: 'Commercial', desc: 'Peak shaving, backup power, and solar integration for offices.', image: '/images/voltide-battery-pack-large.png' },
  { icon: Truck, title: 'Mobile Units', desc: 'Portable BESS for events and remote sites. Up to 1 MWh capacity.', image: '/images/voltide-battery-pack-small.png' },
];

export default function SolutionsPage() {
  return (
    <div data-testid="solutions-page">
      <SEO
        title="Solutions"
        path="/solutions"
        description="Explore Voltide BESS solutions for solar integration, DG replacement, hospitals, restaurants, universities, and EV charging."
      />
      {/* Hero */}
      <section className="py-20 lg:py-28">
        <div className="voltide-container">
          <div className="max-w-3xl animate-fade-up">
            <Badge className="rounded-full px-4 py-1.5 text-xs font-medium bg-blue-50 text-[#0061DD] border-blue-100 mb-6">
              Solutions
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#1A1A1A] leading-[1.1] mb-6">
              Energy solutions for <span className="blue-gradient-text">every need</span>
            </h1>
            <p className="text-base lg:text-lg text-[#57534E] leading-relaxed max-w-2xl">
              From solar integration to diesel generator replacement, Voltide provides tailored BESS solutions for commercial, industrial, and residential applications.
            </p>
          </div>
        </div>
      </section>

      {/* Main Solutions */}
      <section className="pb-20 lg:pb-24" data-testid="main-solutions">
        <div className="voltide-container space-y-8">
          {SOLUTIONS.map((sol, i) => (
            <div key={sol.title} className="bento-card" data-testid={`solution-card-${i}`}>
              <div className={`grid lg:grid-cols-2 gap-0 ${i % 2 === 1 ? 'lg:direction-rtl' : ''}`}>
                <div className="p-8 lg:p-12 flex flex-col justify-center">
                  <div className={`w-12 h-12 rounded-xl ${sol.color === 'amber' ? 'bg-amber-50' : 'bg-blue-50'} flex items-center justify-center mb-5`}>
                    <sol.icon className={`w-6 h-6 ${sol.color === 'amber' ? 'text-amber-600' : 'text-[#0061DD]'}`} />
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] mb-4">{sol.title}</h2>
                  <p className="text-[#57534E] leading-relaxed mb-6">{sol.desc}</p>
                  <ul className="space-y-3 mb-8">
                    {sol.features.map(f => (
                      <li key={f} className="flex items-center gap-3 text-sm text-[#1A1A1A]">
                        <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact" onClick={() => trackEvent(EVENTS.START_RFQ, { source: 'solutions', solution: sol.title })}>
                    <Button className="rounded-full bg-[#0061DD] hover:bg-[#004BB3] text-white gap-2 w-fit" data-testid={`solution-cta-${i}`}>
                      Get a Quote <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                </div>
                <div className="bg-[#F9F9F7] flex items-center justify-center p-8">
                  <img src={sol.image} alt={sol.title} className="sketch-img max-h-80 object-contain" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Sector Solutions */}
      <section className="py-20 lg:py-24 bg-white" data-testid="sector-solutions">
        <div className="voltide-container">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1A1A1A]">
              Sector-specific solutions
            </h2>
            <p className="mt-3 text-base text-[#57534E]">Tailored BESS configurations for every industry</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SECTORS.map((s, i) => (
              <div key={s.title} className="bento-card group" data-testid={`sector-card-${i}`}>
                <div className="aspect-[4/3] overflow-hidden bg-[#F9F9F7]">
                  <img src={s.image} alt={`Voltide BESS for ${s.title}`} className="w-full h-full object-cover sketch-img group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <s.icon className="w-5 h-5 text-[#0061DD]" />
                    <h3 className="text-lg font-bold text-[#1A1A1A]">{s.title}</h3>
                  </div>
                  <p className="text-sm text-[#57534E] leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-24 bg-[#0061DD]" data-testid="solutions-cta">
        <div className="voltide-container text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Not sure which solution fits?
          </h2>
          <p className="text-lg text-blue-100 mb-8 max-w-xl mx-auto">
            Our energy experts will evaluate your needs and recommend the optimal BESS configuration.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button className="rounded-full bg-white text-[#0061DD] hover:bg-blue-50 px-8 py-6 text-lg font-semibold gap-2" data-testid="solutions-cta-quote-btn">
                Talk to Us <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/calculator">
              <Button variant="outline" className="rounded-full border-white/40 text-white hover:bg-white/10 px-8 py-6 text-lg font-medium">
                Calculate Savings
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
